/** Lab tab — historical sold-option strategy analysis (own ledger). */

import "./labView.css";
import {
  fetchLabContractPrices,
  fetchLabContracts,
  fetchLabFilters,
  fetchLabScenarios,
  fetchLabStrategyComparison,
  fetchLabSummary,
  type LabContract,
  type LabFilters,
  type LabScenario,
  type LabStrategyComparison,
  type LabSummary,
} from "./labApi";

const STATE_KEY = "sfe-lab-view-state-v1";

type LabViewState = {
  side: "put" | "call";
  outcome: string;
  ticker: string;
  accountId: string;
  range: "all" | "ytd" | "1y" | "6m" | "3m";
  selectedId: number | null;
  targetPop: number;
  chartPeriod: "1m" | "3m" | "6m" | "1y" | "all";
};

type ChartCandle = { date: string; close: number };

function esc(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function fmtUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "−" : "";
  return `${sign}$${Math.abs(Math.round(value)).toLocaleString("en-US")}`;
}

function fmtPct(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

function fmtPop(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(0)}%`;
}

function loadState(): LabViewState {
  try {
    const raw = JSON.parse(localStorage.getItem(STATE_KEY) || "{}") as Partial<LabViewState>;
    return {
      side: raw.side === "call" ? "call" : "put",
      outcome: raw.outcome || "all",
      ticker: raw.ticker || "",
      accountId: raw.accountId || "",
      range: (raw.range as LabViewState["range"]) || "all",
      selectedId: Number(raw.selectedId) > 0 ? Number(raw.selectedId) : null,
      targetPop: Number(raw.targetPop) > 0 ? Number(raw.targetPop) : 0.85,
      chartPeriod: (["1m", "3m", "6m", "1y", "all"] as const).includes(
        raw.chartPeriod as LabViewState["chartPeriod"],
      )
        ? (raw.chartPeriod as LabViewState["chartPeriod"])
        : "3m",
    };
  } catch {
    return {
      side: "put",
      outcome: "all",
      ticker: "",
      accountId: "",
      range: "all",
      selectedId: null,
      targetPop: 0.85,
      chartPeriod: "3m",
    };
  }
}

/** Sessions before open / after resolution for each chart period chip. */
function chartWindow(period: LabViewState["chartPeriod"]): { before: number; after: number } {
  if (period === "1m") return { before: 10, after: 15 };
  if (period === "6m") return { before: 40, after: 60 };
  if (period === "1y") return { before: 90, after: 120 };
  if (period === "all") return { before: 180, after: 180 };
  return { before: 25, after: 45 };
}

function clipCandles(
  candles: ChartCandle[],
  openDate: string | null,
  resolutionDate: string | null,
  period: LabViewState["chartPeriod"],
): ChartCandle[] {
  if (!candles.length) return candles;
  const { before, after } = chartWindow(period);
  if (period === "all") return candles;
  const anchorOpen = openDate || candles[0]!.date;
  const anchorRes = resolutionDate || openDate || candles[candles.length - 1]!.date;
  const openIdx = candles.findIndex((c) => c.date >= anchorOpen);
  const resIdx = candles.findIndex((c) => c.date >= anchorRes);
  const start = Math.max(0, (openIdx >= 0 ? openIdx : 0) - before);
  const end = Math.min(
    candles.length,
    (resIdx >= 0 ? resIdx : candles.length - 1) + after + 1,
  );
  return candles.slice(start, end);
}

function saveState(state: LabViewState): void {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function rangeToDates(range: LabViewState["range"]): { from?: string; to?: string } {
  if (range === "all") return {};
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const from = new Date(now);
  if (range === "ytd") {
    return { from: `${now.getUTCFullYear()}-01-01`, to };
  }
  const months = range === "1y" ? 12 : range === "6m" ? 6 : 3;
  from.setUTCMonth(from.getUTCMonth() - months);
  return { from: from.toISOString().slice(0, 10), to };
}

function filtersFromState(state: LabViewState): LabFilters {
  const dates = rangeToDates(state.range);
  return {
    side: state.side,
    outcome: state.outcome as LabFilters["outcome"],
    ticker: state.ticker || undefined,
    accountId: state.accountId || undefined,
    from: dates.from,
    to: dates.to,
    targetPop: state.targetPop,
    limit: 500,
    sort: "openDate",
    dir: "desc",
  };
}

function renderScenarios(scenarios: LabScenario[], note: string | null): string {
  if (!scenarios.length) {
    return `<div class="sfe-lab-scenario-note">${esc(note || "No scenarios yet.")}</div>`;
  }
  const head = [
    "Target POP",
    "Strike",
    "Est. POP",
    "Premium",
    "Conv.",
    "Expiry payoff",
    "vs Prem",
    "vs Payoff",
    "Status",
  ];
  return (
    `<div class="sfe-lab-table-wrap"><table class="sfe-lab-table"><thead><tr>` +
    head.map((h) => `<th>${esc(h)}</th>`).join("") +
    `</tr></thead><tbody>` +
    scenarios
      .map((s) => {
        const status = s.available
          ? esc(s.provenance || "ok")
          : esc(s.unavailableReason || "Unavailable");
        return (
          `<tr>` +
          `<td>${fmtPop(s.targetPopPct)}</td>` +
          `<td>${s.strike != null ? s.strike : "—"}</td>` +
          `<td>${fmtPop(s.estimatedPopPct)}</td>` +
          `<td>${fmtUsd(s.premiumEstimate)}</td>` +
          `<td>${esc(s.premiumConvention || "—")}</td>` +
          `<td>${fmtUsd(s.expirationPayoff)}</td>` +
          `<td class="${(s.vsActualPremium ?? 0) >= 0 ? "sfe-lab-pos" : "sfe-lab-neg"}">${fmtUsd(s.vsActualPremium)}</td>` +
          `<td class="${(s.vsActualPayoff ?? 0) >= 0 ? "sfe-lab-pos" : "sfe-lab-neg"}">${fmtUsd(s.vsActualPayoff)}</td>` +
          `<td>${status}</td>` +
          `</tr>`
        );
      })
      .join("") +
    `</tbody></table></div>` +
    (note ? `<div class="sfe-lab-scenario-note">${esc(note)}</div>` : "")
  );
}

function renderComparison(compare: LabStrategyComparison | null): string {
  if (!compare) {
    return `<div class="sfe-lab-compare-note">Loading strategy comparison…</div>`;
  }
  if (!compare.available || !compare.hypothetical) {
    return (
      `<div class="sfe-lab-compare-note"><strong>Strategy comparison</strong> — ${esc(compare.reason || "Unavailable")}. ` +
      `Excluded ${compare.excludedCount} trades. Open contracts to compute historical scenarios, or run backfill-lab-history --scenarios.</div>`
    );
  }
  const h = compare.hypothetical;
  return (
    `<div class="sfe-lab-compare-grid">` +
    `<div class="sfe-lab-kpi"><span class="sfe-lab-kpi-k">Target POP</span><span class="sfe-lab-kpi-v">${fmtPop(compare.targetPopPct)}</span><span class="sfe-lab-kpi-note">${compare.eligibleCount} eligible / ${compare.excludedCount} excluded</span></div>` +
    `<div class="sfe-lab-kpi"><span class="sfe-lab-kpi-k">Alt premium</span><span class="sfe-lab-kpi-v">${fmtUsd(h.premiumCollected)}</span><span class="sfe-lab-kpi-note">Premium given up ${fmtUsd(h.premiumGivenUp)}</span></div>` +
    `<div class="sfe-lab-kpi"><span class="sfe-lab-kpi-k">Alt payoff</span><span class="sfe-lab-kpi-v">${fmtUsd(h.modeledExpirationPayoff)}</span><span class="sfe-lab-kpi-note">Δ vs actual ${fmtUsd(h.payoffDelta)}</span></div>` +
    `<div class="sfe-lab-kpi"><span class="sfe-lab-kpi-k">Alt assign rate</span><span class="sfe-lab-kpi-v">${fmtPct((h.assignmentRate ?? 0) * 100)}</span><span class="sfe-lab-kpi-note">Modeled ITM at expiry spot</span></div>` +
    `</div>` +
    `<div class="sfe-lab-compare-note">Same-universe only. Modeled — not verified executed performance. ${esc((compare.caveats || []).slice(0, 2).join(" "))}</div>`
  );
}

function outcomeClass(outcome: string): string {
  if (outcome === "assigned") return "sfe-lab-outcome-assigned";
  if (outcome === "expired") return "sfe-lab-outcome-expired";
  if (outcome === "closed") return "sfe-lab-outcome-closed";
  return "";
}

function renderKpis(summary: LabSummary | null): string {
  if (!summary) {
    return `<div class="sfe-lab-kpi-strip"><div class="sfe-lab-empty">Loading KPIs…</div></div>`;
  }
  const cards = [
    {
      k: "Sold trades",
      v: String(summary.soldTrades),
      note: `${summary.contractCount} contracts`,
    },
    {
      k: "Assignment rate",
      v: fmtPct((summary.assignmentRate ?? 0) * 100, 1),
      note: summary.assignmentRateLabel,
    },
    {
      k: "Avg opening POP",
      v: fmtPop(summary.averageOpenPopPct),
      note: "Proxy = 1 − |delta|",
    },
    {
      k: "Premium collected",
      v: fmtUsd(summary.premiumCollected),
      note: summary.premiumNote,
    },
    {
      k: "Downside impact",
      v: fmtUsd(summary.downsideImpact),
      note: summary.downsideNote,
    },
    {
      k: "POP coverage",
      v: `${summary.coverage.withRecordedPop}/${summary.soldTrades}`,
      note: `${summary.coverage.missingPop} missing · ${summary.coverage.sampleDenominator}`,
    },
  ];
  return (
    `<div class="sfe-lab-kpi-strip">` +
    cards
      .map(
        (c) =>
          `<div class="sfe-lab-kpi"><span class="sfe-lab-kpi-k">${esc(c.k)}</span>` +
          `<span class="sfe-lab-kpi-v">${esc(c.v)}</span>` +
          `<span class="sfe-lab-kpi-note">${esc(c.note)}</span></div>`,
      )
      .join("") +
    `</div>`
  );
}

function renderExplorer(contracts: LabContract[], selectedId: number | null): string {
  if (!contracts.length) {
    return `<div class="sfe-lab-empty">No sold contracts match these filters.</div>`;
  }
  return (
    `<ul class="sfe-lab-explorer-list">` +
    contracts
      .map((c) => {
        const selected = c.id === selectedId ? " is-selected" : "";
        const move =
          c.priceChangePct == null
            ? "—"
            : `${c.priceChangePct > 0 ? "+" : ""}${c.priceChangePct.toFixed(1)}%`;
        return (
          `<li><button type="button" class="sfe-lab-explorer-item${selected}" data-lab-id="${c.id}">` +
          `<span class="sym">${esc(c.ticker)} ${esc(c.side)} ${c.strike ?? "—"}</span>` +
          `<span class="pop" title="${esc(c.openPopTip || "")}">POP ${esc(fmtPop(c.openPopPct))}</span>` +
          `<span class="meta">${esc(c.openDate)} · ${esc(c.outcome)} · spot ${move}</span>` +
          `<span class="outcome ${outcomeClass(c.outcome)}">${esc(c.outcome)}</span>` +
          `</button></li>`
        );
      })
      .join("") +
    `</ul>`
  );
}

function renderDetail(contract: LabContract | null, subsequent: Record<string, number | null>): string {
  if (!contract) {
    return `<div class="sfe-lab-empty">Select a contract to inspect opening, resolution, and prices.</div>`;
  }
  const rows: [string, string][] = [
    ["Opened", contract.openDate || "—"],
    ["Expiration", contract.expDate || "—"],
    ["Resolved", contract.resolutionDate || "—"],
    ["Contracts", String(contract.contracts)],
    ["Strike", contract.strike != null ? `$${contract.strike}` : "—"],
    ["Premium (gross)", fmtUsd(contract.premium)],
    ["Opening POP", `${fmtPop(contract.openPopPct)} (${contract.openPopLabel})`],
    ["Open spot", contract.openSpot != null ? `$${contract.openSpot.toFixed(2)}` : "—"],
    ["Assign/expiry spot", contract.assignSpot != null ? `$${contract.assignSpot.toFixed(2)}` : "—"],
    ["Spot change", fmtPct(contract.priceChangePct)],
    ["Premium-adj. cost", contract.premiumAdjustedCost != null ? `$${contract.premiumAdjustedCost.toFixed(2)}` : "—"],
    ["Modeled expiry payoff", fmtUsd(contract.expirationPayoff)],
  ];
  const later = (["d1", "d5", "d10", "d20", "d30"] as const)
    .map((k) => {
      const v = subsequent[k];
      return [
        `+${k.slice(1)} sess`,
        v != null ? `$${v.toFixed(2)}` : "—",
      ] as [string, string];
    });
  return (
    rows
      .map(
        ([k, v]) =>
          `<div class="sfe-lab-detail-row"><span>${esc(k)}</span><span>${esc(v)}</span></div>`,
      )
      .join("") +
    `<div class="sfe-lab-detail-section">After resolution</div>` +
    later
      .map(
        ([k, v]) =>
          `<div class="sfe-lab-detail-row"><span>${esc(k)}</span><span>${esc(v)}</span></div>`,
      )
      .join("") +
    `<div class="sfe-lab-detail-section">Note</div>` +
    `<div class="sfe-lab-kpi-note">Premium-adjusted cost is display-only. Ledger avg cost stays strike-based. Assignment spot ≠ strike.</div>`
  );
}

function renderChartPeriodChips(period: LabViewState["chartPeriod"]): string {
  return (
    `<div class="sfe-lab-chart-periods" role="group" aria-label="Chart period">` +
    (["1m", "3m", "6m", "1y", "all"] as const)
      .map(
        (p) =>
          `<button type="button" class="sfe-lab-chart-period${period === p ? " is-active" : ""}" data-lab-chart-period="${p}">${p.toUpperCase()}</button>`,
      )
      .join("") +
    `</div>`
  );
}

function renderChartSvg(
  candles: ChartCandle[],
  opts: {
    strike: number | null;
    openDate: string | null;
    resolutionDate: string | null;
    openSpot: number | null;
    assignSpot: number | null;
    period: LabViewState["chartPeriod"];
    loading?: boolean;
    error?: string | null;
    ticker?: string;
  },
): string {
  const toolbar =
    `<div class="sfe-lab-chart-toolbar">` +
    `<div class="sfe-lab-chart-legend">` +
    `<span><i class="sfe-lab-leg sfe-lab-leg--price"></i>Close</span>` +
    `<span><i class="sfe-lab-leg sfe-lab-leg--strike"></i>Strike</span>` +
    `<span><i class="sfe-lab-leg sfe-lab-leg--open"></i>Open</span>` +
    `<span><i class="sfe-lab-leg sfe-lab-leg--resolve"></i>Resolve</span>` +
    `</div>` +
    renderChartPeriodChips(opts.period) +
    `</div>`;

  if (opts.loading) {
    return toolbar + `<div class="sfe-lab-chart-empty">Loading underlying prices…</div>`;
  }
  if (opts.error) {
    return toolbar + `<div class="sfe-lab-chart-empty sfe-lab-error">${esc(opts.error)}</div>`;
  }

  const view = clipCandles(candles, opts.openDate, opts.resolutionDate, opts.period);
  if (!view.length) {
    return (
      toolbar +
      `<div class="sfe-lab-chart-empty">No underlying price history for this window.` +
      (opts.ticker ? ` (${esc(opts.ticker)})` : "") +
      ` Strike is not the assignment price.</div>`
    );
  }

  const w = 720;
  const h = 300;
  const pad = { l: 48, r: 14, t: 18, b: 32 };
  const closes = view.map((c) => c.close);
  if (opts.strike != null) closes.push(opts.strike);
  if (opts.openSpot != null) closes.push(opts.openSpot);
  if (opts.assignSpot != null) closes.push(opts.assignSpot);
  let min = Math.min(...closes);
  let max = Math.max(...closes);
  const span = Math.max(max - min, 0.01);
  min -= span * 0.1;
  max += span * 0.1;
  const xAt = (i: number) => pad.l + (i / Math.max(view.length - 1, 1)) * (w - pad.l - pad.r);
  const yAt = (v: number) => pad.t + (1 - (v - min) / (max - min)) * (h - pad.t - pad.b);
  const path = view
    .map((c, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(1)} ${yAt(c.close).toFixed(1)}`)
    .join(" ");
  const area =
    `${path} L${xAt(view.length - 1).toFixed(1)} ${h - pad.b} L${xAt(0).toFixed(1)} ${h - pad.b} Z`;
  const openIdx = opts.openDate ? view.findIndex((c) => c.date >= opts.openDate!) : -1;
  const resIdx = opts.resolutionDate
    ? view.findIndex((c) => c.date >= opts.resolutionDate!)
    : -1;

  const strikeLine =
    opts.strike != null
      ? `<line class="sfe-lab-strike-line" x1="${pad.l}" y1="${yAt(opts.strike)}" x2="${w - pad.r}" y2="${yAt(opts.strike)}" stroke="#e67e22" stroke-dasharray="5 4" stroke-width="1.4"/>` +
        `<text x="${w - pad.r - 2}" y="${yAt(opts.strike) - 5}" fill="#e67e22" font-size="10" text-anchor="end">Strike $${opts.strike}</text>`
      : "";

  const mark = (
    idx: number,
    label: string,
    color: string,
    spot: number | null,
  ) => {
    if (idx < 0 || idx >= view.length) return "";
    const x = xAt(idx);
    const y = yAt(spot != null ? spot : view[idx]!.close);
    const tip = `${label} ${view[idx]!.date}${spot != null ? ` · $${spot.toFixed(2)}` : ` · $${view[idx]!.close.toFixed(2)}`}`;
    return (
      `<line x1="${x}" y1="${pad.t}" x2="${x}" y2="${h - pad.b}" stroke="${color}" stroke-opacity="0.4" stroke-dasharray="2 3"/>` +
      `<circle cx="${x}" cy="${y}" r="4.5" fill="${color}" stroke="#0b0f14" stroke-width="1.5"><title>${esc(tip)}</title></circle>` +
      `<text x="${x + 5}" y="${pad.t + 11}" fill="${color}" font-size="10" font-weight="700">${esc(label)}</text>`
    );
  };

  const yTicks = [min, (min + max) / 2, max]
    .map(
      (v) =>
        `<line x1="${pad.l}" y1="${yAt(v)}" x2="${w - pad.r}" y2="${yAt(v)}" stroke="#ffffff12" stroke-width="1"/>` +
        `<text x="${pad.l - 6}" y="${yAt(v) + 3}" fill="#9aa3b2" font-size="10" text-anchor="end">$${v.toFixed(v >= 100 ? 0 : 2)}</text>`,
    )
    .join("");

  const xLabels = [0, Math.floor((view.length - 1) / 2), view.length - 1]
    .filter((i, n, arr) => arr.indexOf(i) === n)
    .map(
      (i) =>
        `<text x="${xAt(i)}" y="${h - 8}" fill="#9aa3b2" font-size="10" text-anchor="middle">${esc(view[i]!.date.slice(5))}</text>`,
    )
    .join("");

  const hoverHits = view
    .map((c, i) => {
      const x = xAt(i);
      const y = yAt(c.close);
      return `<circle class="sfe-lab-chart-hit" cx="${x}" cy="${y}" r="10" data-lab-chart-i="${i}" data-lab-chart-date="${esc(c.date)}" data-lab-chart-close="${c.close}" fill="transparent"/>`;
    })
    .join("");

  return (
    toolbar +
    `<div class="sfe-lab-chart-plot" data-lab-chart-plot>` +
    `<div class="sfe-lab-chart-hover" data-lab-chart-hover hidden></div>` +
    `<svg viewBox="0 0 ${w} ${h}" role="img" aria-label="Underlying price chart for selected contract">` +
    `<defs><linearGradient id="sfe-lab-price-fill" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0%" stop-color="#22d3ee" stop-opacity="0.28"/>` +
    `<stop offset="100%" stop-color="#22d3ee" stop-opacity="0"/>` +
    `</linearGradient></defs>` +
    `<rect x="0" y="0" width="${w}" height="${h}" fill="transparent"/>` +
    yTicks +
    strikeLine +
    `<path d="${area}" fill="url(#sfe-lab-price-fill)"/>` +
    `<path d="${path}" fill="none" stroke="#22d3ee" stroke-width="2.2" stroke-linejoin="round"/>` +
    mark(openIdx, "Open", "#4ade80", opts.openSpot) +
    mark(resIdx, "Resolve", "#f87171", opts.assignSpot) +
    xLabels +
    hoverHits +
    `</svg></div>` +
    `<div class="sfe-lab-chart-caption">Underlying session closes · strike is not assignment/settlement price` +
    (opts.openSpot != null ? ` · open spot $${opts.openSpot.toFixed(2)}` : "") +
    (opts.assignSpot != null ? ` · resolve spot $${opts.assignSpot.toFixed(2)}` : "") +
    `</div>`
  );
}

function renderTable(contracts: LabContract[], selectedId: number | null): string {
  if (!contracts.length) return "";
  const head = [
    "Ticker",
    "Open",
    "Exp",
    "Resolve",
    "Strike",
    "Open spot",
    "Assign spot",
    "Chg %",
    "POP",
    "Quality",
    "Premium",
    "Ct",
    "Outcome",
  ];
  return (
    `<div class="sfe-lab-table-wrap"><table class="sfe-lab-table"><thead><tr>` +
    head.map((h) => `<th>${esc(h)}</th>`).join("") +
    `</tr></thead><tbody>` +
    contracts
      .map((c) => {
        const sel = c.id === selectedId ? " is-selected" : "";
        return (
          `<tr class="${sel}" data-lab-id="${c.id}">` +
          `<td>${esc(c.ticker)}</td>` +
          `<td>${esc(c.openDate)}</td>` +
          `<td>${esc(c.expDate)}</td>` +
          `<td>${esc(c.resolutionDate)}</td>` +
          `<td>${c.strike ?? "—"}</td>` +
          `<td>${c.openSpot != null ? c.openSpot.toFixed(2) : "—"}</td>` +
          `<td>${c.assignSpot != null ? c.assignSpot.toFixed(2) : "—"}</td>` +
          `<td>${fmtPct(c.priceChangePct)}</td>` +
          `<td>${fmtPop(c.openPopPct)}</td>` +
          `<td>${esc(c.openPopLabel)}</td>` +
          `<td>${fmtUsd(c.premium)}</td>` +
          `<td>${c.contracts}</td>` +
          `<td class="${outcomeClass(c.outcome)}">${esc(c.outcome)}</td>` +
          `</tr>`
        );
      })
      .join("") +
    `</tbody></table></div>`
  );
}

export class LabController {
  private panel: HTMLElement | null = null;
  private state = loadState();
  private contracts: LabContract[] = [];
  private summary: LabSummary | null = null;
  private tickers: string[] = [];
  private accounts: string[] = [];
  private subsequent: Record<string, number | null> = {};
  private candles: ChartCandle[] = [];
  private scenarios: LabScenario[] = [];
  private scenarioNote: string | null = null;
  private compare: LabStrategyComparison | null = null;
  private error = "";
  private loading = false;
  private chartLoading = false;
  private chartError: string | null = null;

  mount(panel: HTMLElement): void {
    this.panel = panel;
    panel.classList.add("sfe-lab-panel");
    panel.innerHTML = `<div class="sfe-lab-empty">Loading Lab…</div>`;
    panel.onclick = (event) => this.onClick(event);
    panel.onchange = (event) => this.onChange(event);
    panel.onmousemove = (event) => this.onChartHover(event);
    panel.onmouseleave = () => this.clearChartHover();
  }

  async refresh(_force = false): Promise<void> {
    if (!this.panel || this.loading) return;
    this.loading = true;
    this.error = "";
    try {
      const filters = filtersFromState(this.state);
      const [filterOpts, summaryRes, contractsRes, compare] = await Promise.all([
        fetchLabFilters(),
        fetchLabSummary(filters),
        fetchLabContracts(filters),
        fetchLabStrategyComparison(filters),
      ]);
      this.tickers = filterOpts.tickers;
      this.accounts = filterOpts.accounts;
      this.summary = summaryRes.summary;
      this.contracts = contractsRes.contracts;
      this.compare = compare;
      if (
        this.state.selectedId &&
        !this.contracts.some((c) => c.id === this.state.selectedId)
      ) {
        this.state.selectedId = this.contracts[0]?.id ?? null;
      }
      if (!this.state.selectedId && this.contracts[0]) {
        this.state.selectedId = this.contracts[0].id;
      }
      saveState(this.state);
      await this.loadSelectedExtras();
      this.render();
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
      this.render();
    } finally {
      this.loading = false;
    }
  }

  private async loadSelectedExtras(): Promise<void> {
    const id = this.state.selectedId;
    if (!id) {
      this.candles = [];
      this.subsequent = {};
      this.scenarios = [];
      this.scenarioNote = null;
      this.chartError = null;
      this.chartLoading = false;
      return;
    }
    this.chartLoading = true;
    this.chartError = null;
    // Show chart loading shell immediately while prices fetch.
    this.render();
    try {
      const win = chartWindow(this.state.chartPeriod === "all" ? "all" : "1y");
      const [prices, scenarios] = await Promise.all([
        fetchLabContractPrices(id, win),
        fetchLabScenarios(id),
      ]);
      this.candles = prices.candles ?? [];
      this.subsequent = prices.subsequent ?? {};
      this.chartError =
        prices.unavailable && !this.candles.length
          ? "Underlying price history unavailable for this ticker/window."
          : null;
      this.scenarios = scenarios.scenarios ?? [];
      this.scenarioNote = scenarios.available
        ? scenarios.cached
          ? "Cached historical scenarios."
          : "Computed from historical chain as-of open date."
        : scenarios.reason;
    } catch (err) {
      this.candles = [];
      this.subsequent = {};
      this.scenarios = [];
      this.chartError = err instanceof Error ? err.message : String(err);
      this.scenarioNote = this.chartError;
    } finally {
      this.chartLoading = false;
    }
  }

  private selected(): LabContract | null {
    return this.contracts.find((c) => c.id === this.state.selectedId) ?? null;
  }

  private render(): void {
    if (!this.panel) return;
    if (this.error) {
      this.panel.innerHTML = `<div class="sfe-lab-error">${esc(this.error)}</div>`;
      return;
    }
    const selected = this.selected();
    const tickerOpts =
      `<option value="">All tickers</option>` +
      this.tickers
        .map(
          (t) =>
            `<option value="${esc(t)}"${this.state.ticker === t ? " selected" : ""}>${esc(t)}</option>`,
        )
        .join("");
    const accountOpts =
      `<option value="">All accounts</option>` +
      this.accounts
        .map(
          (a) =>
            `<option value="${esc(a)}"${this.state.accountId === a ? " selected" : ""}>${esc(a)}</option>`,
        )
        .join("");

    this.panel.innerHTML =
      `<div class="sfe-lab-header">` +
      `<div class="sfe-lab-title-block">` +
      `<h2>Lab</h2>` +
      `<p>Would selling puts at a higher opening POP have avoided costly assignments, or mostly reduced premium collected? Distinct from Wheel Lab.</p>` +
      `</div>` +
      `<div class="sfe-lab-filters">` +
      `<label>Range<select data-lab-filter="range">` +
      ["all", "ytd", "1y", "6m", "3m"]
        .map(
          (r) =>
            `<option value="${r}"${this.state.range === r ? " selected" : ""}>${r.toUpperCase()}</option>`,
        )
        .join("") +
      `</select></label>` +
      `<label>Side<select data-lab-filter="side">` +
      `<option value="put"${this.state.side === "put" ? " selected" : ""}>Puts</option>` +
      `<option value="call"${this.state.side === "call" ? " selected" : ""}>Calls</option>` +
      `</select></label>` +
      `<label>Outcome<select data-lab-filter="outcome">` +
      ["all", "assigned", "expired", "closed", "open", "unresolved"]
        .map(
          (o) =>
            `<option value="${o}"${this.state.outcome === o ? " selected" : ""}>${o}</option>`,
        )
        .join("") +
      `</select></label>` +
      `<label>Ticker<select data-lab-filter="ticker">${tickerOpts}</select></label>` +
      `<label>Account<select data-lab-filter="accountId">${accountOpts}</select></label>` +
      `<label>Compare POP<select data-lab-filter="targetPop">` +
      [0.7, 0.8, 0.85, 0.9]
        .map(
          (p) =>
            `<option value="${p}"${this.state.targetPop === p ? " selected" : ""}>${Math.round(p * 100)}%</option>`,
        )
        .join("") +
      `</select></label>` +
      `<button type="button" class="small ghost sfe-lab-assigned-btn" data-lab-action="assigned">Assigned</button>` +
      `</div></div>` +
      renderKpis(this.summary) +
      `<div class="sfe-lab-main">` +
      `<section class="sfe-lab-explorer"><div class="sfe-lab-card-h">Contract explorer</div>${renderExplorer(this.contracts, this.state.selectedId)}</section>` +
      `<section class="sfe-lab-chart-card"><div class="sfe-lab-card-h">Underlying price` +
      `<span>${esc(selected?.ticker || "—")}</span></div>` +
      `<div class="sfe-lab-chart-body">${renderChartSvg(this.candles, {
        strike: selected?.strike ?? null,
        openDate: selected?.openDate ?? null,
        resolutionDate: selected?.resolutionDate ?? null,
        openSpot: selected?.openSpot ?? null,
        assignSpot: selected?.assignSpot ?? null,
        period: this.state.chartPeriod,
        loading: this.chartLoading,
        error: this.chartError,
        ticker: selected?.ticker,
      })}</div></section>` +
      `<section class="sfe-lab-detail"><div class="sfe-lab-card-h">Selected contract</div>` +
      `<div class="sfe-lab-detail-body">${renderDetail(selected, this.subsequent)}</div></section>` +
      `</div>` +
      `<div class="sfe-lab-card-h" style="border:0;padding-left:2px">What-if scenarios</div>` +
      renderScenarios(this.scenarios, this.scenarioNote) +
      `<div class="sfe-lab-card-h" style="border:0;padding-left:2px">Strategy comparison</div>` +
      renderComparison(this.compare) +
      `<div class="sfe-lab-card-h" style="border:0;padding-left:2px">History</div>` +
      renderTable(this.contracts, this.state.selectedId);
  }

  private async onClick(event: MouseEvent): Promise<void> {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const assignedBtn = target.closest<HTMLElement>("[data-lab-action='assigned']");
    if (assignedBtn) {
      this.state.outcome = this.state.outcome === "assigned" ? "all" : "assigned";
      saveState(this.state);
      await this.refresh();
      return;
    }
    const periodBtn = target.closest<HTMLElement>("[data-lab-chart-period]");
    if (periodBtn) {
      const period = periodBtn.dataset.labChartPeriod as LabViewState["chartPeriod"];
      if (period && period !== this.state.chartPeriod) {
        this.state.chartPeriod = period;
        saveState(this.state);
        // Wider periods may need a wider fetch; re-load extras when expanding.
        if (period === "all" || period === "1y" || period === "6m") {
          await this.loadSelectedExtras();
        }
        this.render();
      }
      return;
    }
    const row = target.closest<HTMLElement>("[data-lab-id]");
    if (!row) return;
    const id = Number(row.dataset.labId);
    if (!(id > 0)) return;
    if (id === this.state.selectedId) return;
    this.state.selectedId = id;
    saveState(this.state);
    await this.loadSelectedExtras();
    this.render();
  }

  private onChartHover(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const hit = target?.closest?.(".sfe-lab-chart-hit") as SVGCircleElement | null;
    const hover = this.panel?.querySelector<HTMLElement>("[data-lab-chart-hover]");
    if (!hover) return;
    if (!hit) {
      hover.hidden = true;
      return;
    }
    const date = hit.getAttribute("data-lab-chart-date") || "";
    const close = Number(hit.getAttribute("data-lab-chart-close"));
    const plot = this.panel?.querySelector<HTMLElement>("[data-lab-chart-plot]");
    if (!plot) return;
    const rect = plot.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    hover.hidden = false;
    hover.style.left = `${Math.min(Math.max(x + 12, 8), rect.width - 140)}px`;
    hover.style.top = `${Math.max(y - 36, 8)}px`;
    hover.textContent = `${date}  $${close.toFixed(2)}`;
  }

  private clearChartHover(): void {
    const hover = this.panel?.querySelector<HTMLElement>("[data-lab-chart-hover]");
    if (hover) hover.hidden = true;
  }

  private async onChange(event: Event): Promise<void> {
    const target = event.target as HTMLSelectElement | null;
    if (!target?.dataset.labFilter) return;
    const key = target.dataset.labFilter as keyof LabViewState;
    const value = target.value;
    if (key === "side") this.state.side = value === "call" ? "call" : "put";
    else if (key === "outcome") this.state.outcome = value;
    else if (key === "ticker") this.state.ticker = value;
    else if (key === "accountId") this.state.accountId = value;
    else if (key === "range") this.state.range = value as LabViewState["range"];
    else if (key === "targetPop") this.state.targetPop = Number(value) || 0.85;
    saveState(this.state);
    await this.refresh();
  }
}
